import React, { useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Sample Data for demonstration. This array will be passed via props.
const defaultTimelineData = [
  { year: 2007, title: "Establishment", description: "Founding of the organization." },
  { year: 2008, title: "Initial Expansion", description: "Pokhara & Chitwan Office Expansion." },
  { year: 2010, title: "Portfolio Expansion", description: "New Zealand and the UK markets opened." },
  { year: 2012, title: "NZSA Recognition", description: "Recognized as a New Zealand Specialist Agent." },
  { year: 2014, title: "International Markets", description: "Europe, USA, and Canada portfolios launched." },
  { year: 2015, title: "Birtamode & Butwal", description: "Office Expansion in Birtamode and Butwal." },
  { year: 2017, title: "Sydney Office", description: "On-shore office started in Sydney, Australia." },
  { year: 2018, title: "ENZRA Certification", description: "Certified as Education New Zealand's Recognized Agency." },
  { year: 2019, title: "British Council", description: "British Council Trained Agent Certification achieved." },
  { year: 2020, title: "Tasmania Office", description: "Tasmania, Australia Office Expansion completed." },
  { year: 2021, title: "Adelaide Office", description: "Adelaide, Australia Office Expansion completed." },
  { year: 2022, title: "Perth Office", description: "Perth, Australia Office Expansion completed." },
  { year: 2023, title: "Dharan Expansion", description: "Office Expansion in Dharan." },
  { year: 2024, title: "Damak & Tulsipur", description: "Damak and Tulsipur Office Expansion." },
  
  { year: 2025, title: "Future Vision", description: "Biratnagar and Tulsipur Office Expansion planned." },
];

/**
 * Renders a dynamic, vertical timeline using a single SVG.
 * The events are automatically spaced and placed alternately on the left and right.
 *
 * @param {object[]} timelineData - Array of events with year, title, and description.
 */
const Timeline = ({ timelineData = defaultTimelineData }) => {

  const VIEWBOX_WIDTH = 1920;
  const isMobile = window.innerWidth <= 768;
  const X_CENTER = isMobile ? VIEWBOX_WIDTH / 3.4 : VIEWBOX_WIDTH / 2; 
  

  const data = timelineData.length > 0 ? timelineData : defaultTimelineData;
  const totalEvents = data.length;
  

  const EVENT_SPACING = 80; 
  const PADDING_TOP = 325; 
  const PADDING_BOTTOM = 200; 
  

  const VIEWBOX_HEIGHT = PADDING_TOP + (totalEvents > 0 ? (totalEvents - 1) * EVENT_SPACING : 0) + PADDING_BOTTOM + 200;
  
  // Y-axis constraints
  const Y_START = VIEWBOX_HEIGHT - PADDING_BOTTOM; // Bottom of the line (oldest event)
  const Y_END = PADDING_TOP;    // Top of the line (newest event)
  const Y_SPAN = Y_START - Y_END;
  
  const EVENT_LINE_LENGTH = 120; // Length of the dashed/solid line connecting the center to the text area
  const TEXT_AREA_OFFSET = 10;   // Space between the connecting line end and the text start
  
  const STROKE_COLOR = '#748691';
  const PRIMARY_COLOR = '#86189d'; // Purple color for years

  const events = useMemo(() => {
    if (totalEvents === 0) return [];

    return data.map((event, index) => {
      // Calculate Y position for the event marker
      let y;
      if (totalEvents === 1) {
        // Center the single event
        y = Y_START - Y_SPAN / 2; 
      } else {
        // Distribute events evenly across the Y_SPAN
        y = Y_START - (index / (totalEvents - 1)) * Y_SPAN;
      }

      const isLeft = index % 2 === 0; // Place on the left for even indices, right for odd

      // 1. Center Circle (Timeline Marker)
      const centerCircle = { cx: X_CENTER, cy: y };

      // 2. Connector Line (from center to event block)
      const lineXStart = X_CENTER;
      const lineXEnd = isLeft ? X_CENTER - EVENT_LINE_LENGTH : X_CENTER + EVENT_LINE_LENGTH;

      // 3. Year Text (Bold, primary color, slightly offset from center line)
      const yearTextX = isLeft ? X_CENTER - 20 : X_CENTER + 20;
      
      // 4. Event Text Block (Title and Description)
      const textX = isLeft ? lineXEnd - TEXT_AREA_OFFSET : lineXEnd + TEXT_AREA_OFFSET;
      const textAnchor = isLeft ? 'end' : 'start';

      return {
        id: index,
        ...event,
        y,
        isLeft,
        centerCircle,
        lineXStart,
        lineXEnd,
        yearTextX,
        textX,
        textAnchor
      };
    });
  }, [timelineData, totalEvents, Y_START, Y_SPAN, X_CENTER]);

  // If data is empty, display a message
  if (totalEvents === 0) {
    return (
      <div className="p-8 text-center text-gray-500">
        No timeline data provided.
      </div>
    );
  }

  // Define the Y coordinates for the start and end year large labels
  const firstEventY = events[0]?.y || Y_START;
  const lastEventY = events[totalEvents - 1]?.y || Y_END;
  const firstYear = events[0]?.year || data[0].year;
  const lastYear = events[totalEvents - 1]?.year || data[data.length - 1].year;
  
  // Adjusted Y positions for the large start/end year text
  const LARGE_YEAR_Y_START = firstEventY + 100;
  const LARGE_YEAR_Y_END = lastEventY - 70;

  return (
    <div className="w-full mx-auto p-4 md:p-8 bg-white shadow-xl rounded-xl w-100">
      <div className="">
        <svg 
          id="DynamicTimeline" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox={`0 200 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
          preserveAspectRatio='xMidYMid meet' 
          className="w-full h-auto min-h-[500px]"
       
        >
          {/* Main vertical line */}
          <line 
            x1={X_CENTER} 
            y1={Y_START} 
            x2={X_CENTER} 
            y2={Y_END} 
            style={{ 
              fill: 'none', 
              stroke: STROKE_COLOR, 
              strokeMiterlimit: 10, 
              strokeWidth: '4.7px' 
            }} 
          />
          
          {/* Large Start/End Year Labels */}
          <text 
            x={X_CENTER} 
            y={LARGE_YEAR_Y_START} 
            textAnchor="middle" 
            style={{ fontFamily: 'Proxima Nova Black', fontSize: '58.29px', fill: PRIMARY_COLOR }}
          >
            {firstYear} 
          </text>

          <text 
            x={X_CENTER} 
            y={LARGE_YEAR_Y_END} 
            textAnchor="middle"
            className="font-extrabold" 
            style={{ fontFamily: 'Proxima Nova Black', fontSize: '58.29px', fill: PRIMARY_COLOR }}
          >
            {lastYear}
          </text>

          {/* Render Dynamic Events */}
          {events.map((event, index) => (
            <g key={event.id}>
              {/* Event Connector Line */}
              <line 
                x1={event.lineXStart} 
                y1={event.y} 
                x2={event.lineXEnd} 
                y2={event.y} 
                style={{ 
                  fill: 'none', 
                  stroke: STROKE_COLOR, 
                  strokeMiterlimit: 10, 
                  strokeWidth: '4.7px', 
                  strokeLinecap: 'round',
                  // Alternate between solid and dashed line for visual distinction
                  strokeDasharray:  '8.68 8.68' ,
                }} 
              />
              
              {/* Center Circle Marker */}
              <circle 
                cx={event.centerCircle.cx} 
                cy={event.centerCircle.cy} 
                r="13.18" 
                style={{ fill: PRIMARY_COLOR }} 
              />
              
              {/* Event Year Text */}
              <text 
                x={event.yearTextX} 
                y={event.y - 20} // Slightly above the marker
                textAnchor={event.isLeft ? 'end' : 'start'} 
                className="font-bold"
                style={{ fontFamily: 'Proxima Nova Bold', fontSize: '20.82px', fill: PRIMARY_COLOR }}
              >
                {event.year}
              </text>

              {/* Event Title and Description Text Block */}
                  <foreignObject 
                x={event.isLeft ? event.textX - 500 : event.textX} 
                y={event.y - 18} 
                width="500" 
                height="200"
              >
                <div style={{ 
                  fontFamily: 'Proxima Nova Regular', 
                  fontSize: '22px', 
                  color: '#1F2937',
                  fontWeight: '300',
                  textAlign: event.textAnchor 
                }}>
                  {event.description.replace(/<[^>]*>/g, "")}
                </div>
              </foreignObject>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
};

// Main App component to demonstrate usage
export default Timeline;